import { z } from "zod"

import { revisionSchema } from "./sync.ts"
import { holdReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * `documents`: the original of a receipt, one image or PDF per row, immutable except the one-way `ai_consent` flip (receipts plan 2.1).
 * Request schemas are strict; the stored/response schema is lenient (no `.max()`, no narrowed
 * enums on columns that are plain text in the database).
 */

/** Upload size limit for one original: 10 MiB. */
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024

export const documentKindSchema = z.enum(["receipt"])
export type DocumentKind = z.infer<typeof documentKindSchema>

export const documentSourceSchema = z.enum(["camera", "gallery", "upload", "email"])
export type DocumentSource = z.infer<typeof documentSourceSchema>

/** Camera captures are always JPEG; PDF and HEIC only come from gallery, upload or email. */
export const documentContentTypeSchema = z.enum([
  "image/jpeg",
  "image/png",
  "image/heic",
  "application/pdf",
])
export type DocumentContentType = z.infer<typeof documentContentTypeSchema>

export const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, "sha256 must be 64 lowercase hex")

/** Object path in the private `documents` bucket (ADR-015 section 4): `<organisation_id>/<id>/<sha256>`. */
export function documentStoragePath(organisationId: string, id: string, sha256: string): string {
  return `${organisationId}/${id}/${sha256}`
}

export const documentSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  kind: documentKindSchema,
  source: documentSourceSchema,
  // Lenient: plain text in the database.
  content_type: z.string(),
  sha256: z.string(),
  bytes: z.number().int().nonnegative(),
  storage_path: z.string(),
  captured_at: z.string().datetime({ offset: true }),
  /** Server-owned: set once the object exists with the declared size. */
  uploaded_at: z.string().datetime({ offset: true }).nullable().default(null),
  /** The user agreed to AI reading (PRD R7.4); false = stored but never sent to a provider. May flip false -> true once. */
  ai_consent: z.boolean().default(false),
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: holdReasonSchema.nullable().default(null),
  revision: revisionSchema,
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Document = z.infer<typeof documentSchema>

/**
 * Create request. `organisation_id` is filled by the server, so the full `storage_path`
 * equality is checked there; the shape and the `<id>/<sha256>` tail are checked here.
 */
export const createDocumentRequestSchema = z
  .object({
    id: z.uuid().optional(),
    kind: documentKindSchema.default("receipt"),
    source: documentSourceSchema,
    content_type: documentContentTypeSchema,
    sha256: sha256Schema,
    bytes: z.number().int().min(1).max(DOCUMENT_MAX_BYTES),
    storage_path: z.string().regex(/^[0-9a-f-]{36}\/[0-9a-f-]{36}\/[0-9a-f]{64}$/),
    captured_at: z.string().datetime({ offset: true }),
    ai_consent: z.boolean().default(false),
  })
  .superRefine((request, ctx) => {
    const [, idPart, shaPart] = request.storage_path.split("/")
    if (shaPart !== request.sha256) {
      ctx.addIssue({
        code: "custom",
        message: "storage_path must end with the sha256",
        path: ["storage_path"],
      })
    }
    if (request.id !== undefined && idPart !== request.id) {
      ctx.addIssue({
        code: "custom",
        message: "storage_path must contain the document id",
        path: ["storage_path"],
      })
    }
  })

export type CreateDocumentRequest = z.input<typeof createDocumentRequestSchema>
export type ParsedCreateDocumentRequest = z.infer<typeof createDocumentRequestSchema>

/**
 * The only update a document accepts (owner decision 2026-10-21): the user consents to AI
 * reading after the original was already stored. One way only, false -> true; true -> false and
 * every other field stay immutable. `ai_consent: true` on a document that already has it is an
 * idempotent no-op. Sync push `update` fields are exactly `{ "ai_consent": true }`
 * (`base_revision` rides on the entry).
 */
export const updateDocumentRequestSchema = z.strictObject({ ai_consent: z.literal(true) })
export type UpdateDocumentRequest = z.infer<typeof updateDocumentRequestSchema>

/** Any other document update: sync push answers `update` with this rejection reason. */
export const DOCUMENT_IMMUTABLE_REASON = "document_immutable"
/** `details.reason` of the CONFLICT when the organisation already holds this sha256. */
export const DOCUMENT_DUPLICATE_FILE_REASON = "duplicate_file"
/**
 * `details.reason` of the VALIDATION_FAILED (422) the blob PUT answers when the file does not match what
 * `upload-url` declared. All four are permanent for that file: a client must fix the file (for
 * `exif_not_stripped`: re-encode without camera metadata before hashing), not retry the same bytes.
 */
export const DOCUMENT_BLOB_REASON = {
  sizeMismatch: "size_mismatch",
  contentTypeMismatch: "content_type_mismatch",
  sha256Mismatch: "sha256_mismatch",
  exifNotStripped: "exif_not_stripped",
} as const

/** POST /api/v1/documents/:id/upload-url */
export const documentUploadUrlRequestSchema = z.object({
  sha256: sha256Schema,
  bytes: z.number().int().min(1).max(DOCUMENT_MAX_BYTES),
  content_type: documentContentTypeSchema,
})
export type DocumentUploadUrlRequest = z.infer<typeof documentUploadUrlRequestSchema>

export const documentUploadUrlResponseSchema = z.object({
  upload_url: z.string().min(1),
  storage_path: z.string().min(1),
  expires_at: z.string().datetime({ offset: true }),
})
export type DocumentUploadUrlResponse = z.infer<typeof documentUploadUrlResponseSchema>

/** GET /api/v1/documents/:id/download-url (15 minute signed URL, not entitlement-guarded). */
export const documentDownloadUrlResponseSchema = z.object({
  download_url: z.string().min(1),
  expires_at: z.string().datetime({ offset: true }),
})
export type DocumentDownloadUrlResponse = z.infer<typeof documentDownloadUrlResponseSchema>
